import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'copyVoterListCheckbox',
    'cantBeCopiedHelpText',
    'isTestRadio',
    'errorMessage',
    'toggleButton',
    'singleDuplicateElements',
    'singleDuplicateButtons',
    'multipleDuplicateElements',
    'multipleDuplicateButtons',
    'multipleDuplicateButtonsTable'
  ];

  declare copyVoterListCheckboxTarget: HTMLInputElement;
  declare cantBeCopiedHelpTextTarget: HTMLElement;
  declare isTestRadioTarget: HTMLInputElement;
  declare errorMessageTarget: HTMLElement;
  declare toggleButtonTarget: HTMLElement;

  declare singleDuplicateElementsTarget: HTMLElement;
  declare singleDuplicateButtonsTarget: HTMLElement;

  declare multipleDuplicateElementsTarget: HTMLElement;
  declare multipleDuplicateButtonsTarget: HTMLElement;
  declare multipleDuplicateButtonsTableTarget: HTMLElement;

  private voterListUnusableForTest!: boolean;
  private previousCopyVoterListCheckboxValue?: boolean;
  private mode: 'single' | 'multiple' = 'single';
  private isDirty = false;

  // Voting Time fields are merged into every row's validation server-side, so their
  // errors arrive attributed to a row too — this regex identifies those to strip the
  // row attribution before display, since they're shared/form-level, not row data.
  private readonly timerRelatedKeywords = /end date|countdown timer|voting duration/i;

  connect() {
    this.voterListUnusableForTest =
      parseInt((this.element as HTMLElement).dataset.sourceVoterListLength!, 10) > 5;

    if (this.voterListUnusableForTest && this.isTestRadioTarget.checked) {
      this.copyVoterListCheckboxTarget.checked = false;
      this.copyVoterListCheckboxTarget.disabled = true;
    }

    const form = this.element.querySelector('form');
    if (!form) return;

    form.querySelectorAll('input, textarea, select').forEach((el) => {
      el.addEventListener('input', () => {
        this.isDirty = true;
      });
    });

    const parentDiv = document.getElementById('multiple_duplicate_component');
    if (parentDiv) {
      parentDiv.addEventListener('handsontable-changed', (e: any) => {
        this.isDirty = true;
      });
    }

    // Voting Time fields live outside the grid, but their values are merged into
    // every row server-side (see Vote::MultipleDuplicate::Input#filtered_attributes)
    // — a change here must re-trigger the growable grid's background validation the
    // same way a cell commit does, since onCellValueChanged never fires for these.
    const votingTimeForm = document.getElementById('voting-time-form');
    if (votingTimeForm) {
      votingTimeForm.addEventListener('change', () => {
        document.dispatchEvent(new CustomEvent('revalidate-rows'));
      });
    }

    form.addEventListener('submit', () => {
      this.isDirty = false;
    });

    window.addEventListener('beforeunload', (e) => {
      if (!this.isDirty) return;
      e.preventDefault();
    });
  }

  updateForm(event: Event) {
    if (!this.voterListUnusableForTest) return;

    const target = event.target as HTMLInputElement;
    const isTest = target.checked && target.value === 'true';

    if (isTest) {
      this.previousCopyVoterListCheckboxValue = this.copyVoterListCheckboxTarget.checked;
      this.copyVoterListCheckboxTarget.checked = false;
      this.copyVoterListCheckboxTarget.disabled = true;
      this.cantBeCopiedHelpTextTarget.classList.remove('hidden');
    } else {
      this.copyVoterListCheckboxTarget.checked =
        this.previousCopyVoterListCheckboxValue ?? false;
      this.copyVoterListCheckboxTarget.disabled = false;
      this.cantBeCopiedHelpTextTarget.classList.add('hidden');
    }
  }

  toggleMode() {
    if (this.mode === 'single') {
      this.mode = 'multiple';
      this.showMultipleMode();
    } else {
      this.mode = 'single';
      this.showSingleMode();
    }
  }

  handleError(event: any) {
    const [data, _status, _xhr] = event.detail;

    this.errorMessageTarget.innerText = '';
    this.errorMessageTarget.classList.add('hidden');

    if (!data.errors || data.errors.length === 0) return;

    const tableErrors: { row?: number; cols?: number[]; errors: string[] }[] = [];
    const repeatErrorMessages = new Set<string>();

    data.errors.forEach((error: { row?: number; cols?: number[]; errors: string[] }) => {
      const relevantMessages = error.errors.filter(msg =>
        this.timerRelatedKeywords.test(msg)
      );

      const otherMessages = error.errors.filter(msg =>
        !this.timerRelatedKeywords.test(msg)
      );

      if (otherMessages.length > 0) {
        tableErrors.push({ ...error, errors: otherMessages });
      }

      relevantMessages.forEach(msg => repeatErrorMessages.add(msg));
    });

    tableErrors.forEach(error => {
      if (typeof error.row === 'number' && Array.isArray(error.cols)) {
        // Carried per-cell so the growable path can also show it as a tooltip.
        const message = error.errors.join(' || ');
        const errorCells = error.cols.map(col => ({ row: error.row!, col, message }));
        this.sendHighlightMultiple(errorCells);
      }
    });

    const formattedTableErrors = tableErrors
      .map(error => {
        const messages = error.errors.join(' || ');
        return typeof error.row === 'number'
          ? `Row ${error.row + 1}: ${messages}`
          : messages;
      })
      .filter(Boolean)
      .join('\n');

    const uniqueRepeatErrors = Array.from(repeatErrorMessages).join('\n');

    const allMessages = [formattedTableErrors, uniqueRepeatErrors]
      .filter(Boolean)
      .join('\n');

    if (allMessages) {
      this.errorMessageTarget.innerText =
        `Please fix the following errors:\n${allMessages}`;
      this.errorMessageTarget.classList.remove('hidden');
    }
  }


  // Hover-only tooltips were rejected as UX (no visible signal without guessing to hover).
  handleGrowableRowErrors(event: CustomEvent<{ errors: { row: number; message: string }[] }>) {
    const errors = event.detail?.errors ?? [];

    if (errors.length === 0) {
      this.errorMessageTarget.innerText = '';
      this.errorMessageTarget.classList.add('hidden');
      return;
    }

    const rowMessages: string[] = [];
    const repeatErrorMessages = new Set<string>();

    // Each row's message is a " || "-joined bundle (see applyErrorMap) that may mix
    // row data errors with shared Voting Time errors — split out only the shared ones
    // (to show undecorated, once), and rejoin the rest into a single Row N: line.
    errors.forEach(({ row, message }) => {
      const parts = message.split(' || ');
      const rowParts = parts.filter(part => !this.timerRelatedKeywords.test(part));
      const sharedParts = parts.filter(part => this.timerRelatedKeywords.test(part));

      if (rowParts.length > 0) rowMessages.push(`Row ${row + 1}: ${rowParts.join(' || ')}`);
      sharedParts.forEach(part => repeatErrorMessages.add(part));
    });

    const allMessages = [...rowMessages, ...Array.from(repeatErrorMessages)].join('\n');

    this.errorMessageTarget.innerText = `Please fix the following errors:\n${allMessages}`;
    this.errorMessageTarget.classList.remove('hidden');
  }

  sendHighlightMultiple(cells: { row: number, col: number, message?: string }[]) {
    const tableElement = document.getElementById('duplicate-table')
    if (tableElement) {
      const event = new CustomEvent('highlight-multiple-cells', {
        detail: { cells }
      })
      tableElement.dispatchEvent(event)
    }

    // No-op for the legacy Handsontable path, which only ever listens on #duplicate-table.
    document.dispatchEvent(new CustomEvent('highlight-multiple-cells', {
      detail: { cells }
    }))
  }

  fillFirstRow(data: { [key: string]: string }) {
    const tableElement = document.getElementById('duplicate-table');
    if (tableElement) {
      const event = new CustomEvent('fill-first-row', {
        detail: { firstRow: data }
      });
      tableElement.dispatchEvent(event);
    }

    // No-op for the legacy Handsontable path, which only ever listens on #duplicate-table.
    document.dispatchEvent(new CustomEvent('fill-first-row', {
      detail: { firstRow: data }
    }));
  }

  // No-op for the legacy path since that coffee file never listens on `document`.
  addRows() {
    document.dispatchEvent(new CustomEvent('add-rows'));
  }

  clearRows() {
    document.dispatchEvent(new CustomEvent('clear-rows'));
  }

  private showSingleMode() {
    this.multipleDuplicateElementsTarget.classList.add('hidden-table');
    this.multipleDuplicateButtonsTarget.classList.add('hidden');
    this.multipleDuplicateButtonsTableTarget.classList.add('hidden');

    this.singleDuplicateElementsTarget.classList.remove('hidden');
    this.singleDuplicateButtonsTarget.classList.remove('hidden');

    this.toggleButtonTarget.textContent = 'Multiple Motions';

    const duplicateFormElement = document.getElementById('duplicate_form') as HTMLFormElement;
    if (duplicateFormElement) duplicateFormElement.action = '/votes/duplicate';

    const hotWrapper = document.getElementById('multipleDuplicateTable') as HTMLElement;
    if (hotWrapper) hotWrapper.style.display = 'none';

    this.isDirty = false;
  }

  private showMultipleMode() {
    this.singleDuplicateElementsTarget.classList.add('hidden');
    this.singleDuplicateButtonsTarget.classList.add('hidden');

    this.multipleDuplicateElementsTarget.classList.remove('hidden-table');
    this.multipleDuplicateButtonsTarget.classList.remove('hidden');
    this.multipleDuplicateButtonsTableTarget.classList.remove('hidden');

    this.toggleButtonTarget.textContent = 'Single Motion';
    this.toggleButtonTarget.classList.add('hidden');

    const duplicateFormElement = document.getElementById('duplicate_form') as HTMLFormElement;
    if (duplicateFormElement) duplicateFormElement.action = '/votes/multiple_duplicate';

    const hotWrapper = document.getElementById('multipleDuplicateTable') as HTMLElement;
    if (hotWrapper) hotWrapper.style.display = '';

    this.isDirty = false;

    const keyField = document.getElementById('key_field') as HTMLInputElement;
    const voteTitleField = document.getElementById('vote_title_field') as HTMLInputElement;
    const ballotQuestionField = document.getElementById('ballot_question_field') as HTMLInputElement;

    const firstRowData = {
      vote_key: keyField ? keyField.value : '',
      vote_title: voteTitleField ? voteTitleField.value : '',
      ballot_question_title: ballotQuestionField ? ballotQuestionField.value : ''
    };

    this.fillFirstRow(firstRowData);
  }
}
